import { FieldSelect } from '@components/field-builder/field-select';
import { ModalContainer } from '@components/modal/modal';
import { ReuploadReason } from '@dtos/reupload-reason';
import { ClientTrackingCategory } from '@models/client-tracking-category';
import { FieldType } from '@models/field-builder/field-type';
import { LabelPosition } from '@models/field-builder/label-position';
import {
    ErrorText,
    SpacedText
} from '@screens/review-section3/review/styles';
import {
    createTrackingService,
    createTranslationService
} from '@services/factories';
import { IReuploadDocumentModalStore } from '@stores/modal/reupload-documents-modal-store';
import { BaseModalContainer } from '@styles/container-styles';
import { cacheTranslations } from '@utils/cache-translations';
import { observer } from 'mobx-react';
import * as React from 'react';

interface IProps {
    store: IReuploadDocumentModalStore;
    trackingCategory: ClientTrackingCategory;
    onReuploadSuccess?: () => void;
}

enum ReuploadDocumentModalTranslation {
    modalTitle = 'modalTitle',
    selectReasonInstruction = 'selectReasonInstruction',
    reasonLabel = 'reasonLabel',
    reasonPlaceholder = 'reasonPlaceholder',
    cancelButton = 'cancelButton',
    confirmButton = 'confirmButton',
    reuploadReasonDocumentDoesNotMatch = 'reuploadReasonDocumentDoesNotMatch',
    reuploadReasonCopyNotLegible = 'reuploadReasonCopyNotLegible',
    reuploadReasonDocumentExpired = 'reuploadReasonDocumentExpired',
    reuploadReasonPictureIsNotApplicant = 'reuploadReasonPictureIsNotApplicant'
}

enum GlobalTranslation {
    requestFailure = 'requestFailure'
}

interface ReuploadReasonSelectOption {
    key: ReuploadReason;
    label: string;
}

@observer
class ReuploadDocumentsModal extends React.Component<IProps> {
    static readonly translationContext: string = 'reuploadDocuments';
    // Probably good idea to dependency inject these later
    trackingService = createTrackingService();

    private readonly translations: Record<keyof typeof ReuploadDocumentModalTranslation, string>;
    private readonly reuploadReasonOptions: ReuploadReasonSelectOption[];
    private readonly globalTranslations = cacheTranslations(
        createTranslationService(),
        GlobalTranslation
    );

    constructor(props) {
        super(props);

        this.translations = cacheTranslations(
            createTranslationService(),
            ReuploadDocumentModalTranslation,
            ReuploadDocumentsModal.translationContext
        );

        this.reuploadReasonOptions = [
            {
                key: ReuploadReason.DocumentDoesNotMatch,
                label: this.translations.reuploadReasonDocumentDoesNotMatch
            },
            {
                key: ReuploadReason.CopyNotLegible,
                label: this.translations.reuploadReasonCopyNotLegible
            },
            {
                key: ReuploadReason.DocumentExpired,
                label: this.translations.reuploadReasonDocumentExpired
            },
            {
                key: ReuploadReason.PictureIsNotApplicant,
                label: this.translations.reuploadReasonPictureIsNotApplicant
            }
        ];
    }

    handleReasonSelected = (reason: ReuploadReason) => {
        this.props.store.setSelectedReason(reason);
    }

    handleConfirmModal = async () => {
        await this.props.store.requestReuploadAsync();

        // todo: track reupload i9 client events
        if (this.props.store.reuploadRequestStatus === 'success') {
            this.props.store.closeModal();
            this.props.onReuploadSuccess?.();
        }
    };

    renderModalContent() {
        const { store } = this.props;

        // todo: validation and request errors
        return (
            <BaseModalContainer>
                <SpacedText>
                    {
                        this.translations.selectReasonInstruction
                    }
                </SpacedText>
                <FieldSelect type={FieldType.SELECT}
                             labelPosition={LabelPosition.TOP}
                             label={this.translations.reasonLabel}
                             id={'reupload-reason'}
                             placeHolder={this.translations.reasonPlaceholder}
                             setValueMethod={this.handleReasonSelected} value={store.selectedReason}
                             options={this.reuploadReasonOptions} />
                {
                    store.reuploadRequestStatus === 'error' && (
                        <ErrorText>{this.globalTranslations.requestFailure}</ErrorText>
                    )
                }
            </BaseModalContainer>
        );
    }

    handleCancelModal = () => {
        this.props.store.closeModal();
    }

    render() {
        const { store } = this.props;

        if (!store.showModal) {
            return null;
        }

        const modalContent = this.renderModalContent();
        return (
            <ModalContainer title={this.translations.modalTitle}
                            isOpen={store.showModal}
                            onCancelClickHandler={this.handleCancelModal}
                            onConfirmClickHandler={this.handleConfirmModal}
                            cancelLabel={this.translations.cancelButton}
                            confirmLabel={this.translations.confirmButton}>
                { modalContent }
            </ModalContainer>
        );
    }
}

export default ReuploadDocumentsModal;



// WEBPACK FOOTER //
// ./src/screens/review-section2/reupload-documents-modal.tsx